import React from 'react';
import type { AggregatedRow } from './types';
import { formatMetric } from './utils';

type SortKey = 'label' | 'metricValue' | 'sharePct' | 'count';
type SortDirection = 'asc' | 'desc';

type AdvancedDataTableProps = {
  rows: AggregatedRow[];
  dimensionLabel: string;
  metricLabel: string;
  loading?: boolean;
  onRowClick?: (row: AggregatedRow) => void;
  isRowSelected?: (row: AggregatedRow) => boolean;
};

const COLOR_PALETTE = ['#1f4bd6', '#e53935', '#f59e0b', '#7c3aed', '#10b981', '#64748b'];

function SortIndicator({ active, direction }: { active: boolean; direction: SortDirection }) {
  if (!active) return <span className="ml-1 text-gray-300">↕</span>;
  return <span className="ml-1">{direction === 'asc' ? '▲' : '▼'}</span>;
}

export function AdvancedDataTable({
  rows,
  dimensionLabel,
  metricLabel,
  loading,
  onRowClick,
  isRowSelected,
}: AdvancedDataTableProps) {
  const [sortKey, setSortKey] = React.useState<SortKey>('metricValue');
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('desc');
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [searchText, setSearchText] = React.useState('');
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection(key === 'label' ? 'asc' : 'desc');
    }
  };

  const processedRows = React.useMemo(() => {
    let result = rows;
    if (searchText.trim()) {
      const query = searchText.trim().toLowerCase();
      result = result.filter(
        row =>
          row.label.toLowerCase().includes(query) ||
          (row.secondaryLabel && row.secondaryLabel.toLowerCase().includes(query))
      );
    }
    return [...result].sort((a, b) => {
      // "OTROS" always goes last regardless of sort direction
      if (a.isOthers) return 1;
      if (b.isOthers) return -1;
      const dir = sortDirection === 'asc' ? 1 : -1;
      if (sortKey === 'label') return dir * a.label.localeCompare(b.label);
      return dir * (a[sortKey] - b[sortKey]);
    });
  }, [rows, sortKey, sortDirection, searchText]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-xs text-gray-500">
        <span className="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
        <span className="ml-2">Cargando...</span>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="flex h-64 items-center justify-center text-xs text-gray-500">Sin datos</div>
    );
  }

  const totalMetric = rows.reduce((sum, row) => sum + row.metricValue, 0);

  const thClass =
    'px-2 py-1.5 cursor-pointer select-none transition hover:text-gray-800 text-[11px]';

  return (
    <div className="relative max-h-[420px] min-h-[200px] overflow-auto rounded-lg border border-gray-200">
      <table className="min-w-[680px] text-[11px]">
        <thead className="sticky top-0 z-[1] bg-gray-50 uppercase text-gray-500">
          <tr>
            <th className="px-2 py-1.5 text-left text-[11px]">#</th>
            <th className={`${thClass} text-left`} onClick={() => handleSort('label')}>
              <span className="inline-flex items-center gap-1">
                {dimensionLabel}
                <SortIndicator active={sortKey === 'label'} direction={sortDirection} />
                <button
                  type="button"
                  className={`ml-1 inline-flex items-center rounded p-0.5 transition ${searchOpen ? 'bg-primary/10 text-primary' : 'text-gray-400 hover:text-gray-600'}`}
                  onClick={event => {
                    event.stopPropagation();
                    setSearchOpen(prev => !prev);
                    if (searchOpen) setSearchText('');
                  }}
                  aria-label="Buscar dimensión"
                >
                  <svg
                    className="h-3 w-3"
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="9" cy="9" r="5" />
                    <path d="M13 13L17 17" strokeLinecap="round" />
                  </svg>
                </button>
              </span>
              {searchOpen && (
                <input
                  ref={searchInputRef}
                  type="text"
                  className="mt-1 block w-full rounded border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-normal normal-case text-gray-800 placeholder:text-gray-400"
                  placeholder="Filtrar..."
                  value={searchText}
                  onClick={event => event.stopPropagation()}
                  onChange={event => setSearchText(event.target.value)}
                />
              )}
            </th>
            <th className={`${thClass} text-right`} onClick={() => handleSort('metricValue')}>
              {metricLabel}
              <SortIndicator active={sortKey === 'metricValue'} direction={sortDirection} />
            </th>
            <th className={`${thClass} text-right`} onClick={() => handleSort('sharePct')}>
              %
              <SortIndicator active={sortKey === 'sharePct'} direction={sortDirection} />
            </th>
            <th className={`${thClass} text-right`} onClick={() => handleSort('count')}>
              Conteo
              <SortIndicator active={sortKey === 'count'} direction={sortDirection} />
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {processedRows.map((row, index) => {
            const selected = isRowSelected?.(row) ?? false;
            return (
              <tr
                key={row.key}
                onClick={() => onRowClick?.(row)}
                className={`transition ${onRowClick ? 'cursor-pointer' : ''} ${
                  selected ? 'bg-primary/10' : 'hover:bg-gray-50'
                }`}
              >
                <td className="w-4 px-2 py-1">
                  <span
                    className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-medium text-white"
                    style={{ backgroundColor: COLOR_PALETTE[index % COLOR_PALETTE.length] }}
                  >
                    {index + 1}
                  </span>
                </td>
                <td className="px-2 py-1 text-gray-900">
                  <div className="flex flex-col">
                    <span
                      className="max-w-[20rem] truncate whitespace-nowrap font-medium"
                      title={row.label}
                    >
                      {row.label}
                    </span>
                    {row.secondaryLabel ? (
                      <span
                        className="max-w-[20rem] truncate whitespace-nowrap text-[10px] text-gray-500"
                        title={row.secondaryLabel}
                      >
                        {row.secondaryLabel}
                      </span>
                    ) : null}
                  </div>
                </td>
                <td className="px-2 py-1 text-right font-semibold text-primary">
                  {formatMetric(row.metricValue)}
                </td>
                <td className="px-2 py-1 text-right text-gray-500">{row.sharePct.toFixed(2)}%</td>
                <td className="px-2 py-1 text-right text-gray-500">
                  {row.count.toLocaleString('es-CO')}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot className="sticky bottom-0 z-[1] border-t border-gray-200 bg-gray-50">
          <tr className="text-[11px] font-semibold uppercase text-gray-600">
            <td className="px-2 py-1.5" colSpan={2}>
              Total
            </td>
            <td className="px-2 py-1.5 text-right text-primary">{formatMetric(totalMetric)}</td>
            <td className="px-2 py-1.5 text-right">100%</td>
            <td className="px-2 py-1.5 text-right">-</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
